/**
 * @file Ecran Missions : quotidiennes / hebdo / succes (progression, reclamer), passe de saison 25 paliers (gratuit + epic), cadeau 7 jours.
 */
import type { MissionProgress } from '@core/index';
import { MS_PER_DAY, stageValueToDisplay, weekKey } from '@core/index';
import { formatBig, formatDuration } from '@shared/format';
import { Component } from '../core/Component';
import { h, setText, setVar, toggleClass } from '../core/dom';
import { send, uiHints } from '../core/feedback';
import { makeTabs, rewardChips, sheetHeader, walletChips } from '../core/widgets';
import type { TabsHandle } from '../core/widgets';
import type { UiContext } from '../UiContext';

type TabId = 'missions' | 'pass' | 'daily';
type MissionKind = 'daily' | 'weekly' | 'achievements';

interface Section {
  kind: MissionKind;
  list: HTMLElement;
  reset: HTMLElement | null;
  empty: HTMLElement;
  cache: Map<string, HTMLElement>;
}

interface RowParts {
  bar: HTMLElement;
  value: HTMLElement;
  btn: HTMLElement;
}

interface PassCell {
  tier: number;
  track: 'free' | 'epic';
  root: HTMLElement;
}

const TAB_IDS: readonly TabId[] = ['missions', 'pass', 'daily'];

export class MissionsScreen extends Component {
  private tabs!: TabsHandle<TabId>;
  private readonly panes = new Map<TabId, HTMLElement>();
  private readonly sections: Section[] = [];
  private readonly rowParts = new WeakMap<HTMLElement, RowParts>();

  // pass
  private passBody!: HTMLElement;
  private passLocked!: HTMLElement;
  private passTier!: HTMLElement;
  private passBar!: HTMLElement;
  private passXp!: HTMLElement;
  private passEnds!: HTMLElement;
  private passEpicBtn!: HTMLElement;
  private passScroller!: HTMLElement;
  private readonly passCells: PassCell[] = [];
  private readonly passCols: HTMLElement[] = [];
  private passScrolled = false;

  // daily
  private dailyCells: HTMLElement[] = [];
  private dailyBtn!: HTMLElement;
  private dailyHint!: HTMLElement;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'screen missions');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    const initial = uiHints.missionsTab ?? 'missions';
    uiHints.missionsTab = null;
    this.tabs = makeTabs<TabId>(
      [
        { id: 'missions', label: t.t('missions.title') },
        { id: 'pass', label: t.t('pass.title') },
        { id: 'daily', label: t.t('daily.title') },
      ],
      initial,
      () => this.onTab(),
    );

    const body = h('div', { class: 'sheet-body' });
    for (const id of TAB_IDS) {
      const pane = h('div', { class: `pane pane-${id}` });
      this.panes.set(id, pane);
      body.append(pane);
    }
    this.buildMissions(this.panes.get('missions') as HTMLElement);
    this.buildPass(this.panes.get('pass') as HTMLElement);
    this.buildDaily(this.panes.get('daily') as HTMLElement);

    this.el.append(sheetHeader(this.ctx, t.t('missions.title')), this.tabs.el, body);
    this.onTab();
  }

  // ------------------------------------------------------------------------------------------------ missions

  private buildMissions(pane: HTMLElement): void {
    const t = this.ctx.i18n;
    const defs: Array<{ kind: MissionKind; title: string; withReset: boolean }> = [
      { kind: 'daily', title: t.t('missions.daily'), withReset: true },
      { kind: 'weekly', title: t.t('missions.weekly'), withReset: true },
      { kind: 'achievements', title: t.t('missions.achievements'), withReset: false },
    ];
    for (const d of defs) {
      const reset = d.withReset ? h('span', { class: 'section-reset' }) : null;
      const list = h('div', { class: 'mission-list' });
      const empty = h('p', { class: 'hint' }, t.t('missions.empty'));
      pane.append(h('section', { class: 'mission-section' }, h('div', { class: 'section-head' }, h('h3', {}, d.title), reset), list, empty));
      this.sections.push({ kind: d.kind, list, reset, empty, cache: new Map() });
    }
  }

  private createRow(m: MissionProgress): HTMLElement {
    const bar = h('div', { class: 'progress' });
    const value = h('span', { class: 'mission-value' });
    const btn = h('button', { class: 'btn small mission-claim', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'mission.claim', id: m.id }) });
    const def = this.ctx.index.mission(m.id);
    const name = this.ctx.i18n.name(def.name).replace('{n}', formatBig(m.target));
    const root = h(
      'div',
      { class: 'mission-row panel', 'data-mission': m.id },
      h('div', { class: 'mission-main' }, h('div', { class: 'mission-name' }, name), h('div', { class: 'mission-barwrap' }, bar, value), h('div', { class: 'chips-row' }, ...rewardChips(this.ctx, def.reward))),
      btn,
    );
    this.rowParts.set(root, { bar, value, btn });
    return root;
  }

  private updateRow(el: HTMLElement, m: MissionProgress): void {
    const p = this.rowParts.get(el);
    if (!p) return;
    const done = m.progress >= m.target;
    setVar(p.bar, '--progress', (m.target > 0 ? Math.min(1, m.progress / m.target) : 0).toFixed(3));
    setText(p.value, `${formatBig(Math.min(m.progress, m.target))}/${formatBig(m.target)}`);
    toggleClass(el, 'done', done);
    toggleClass(el, 'claimed', m.claimed);
    toggleClass(p.btn, 'disabled', m.claimed || !done);
    toggleClass(p.btn, 'ready', done && !m.claimed);
    setText(p.btn, m.claimed ? this.ctx.i18n.t('common.claimed') : this.ctx.i18n.t('common.claim'));
  }

  private updateMissions(): void {
    const { missions } = this.ctx.sim.data;
    const now = Date.now();
    const { i18n } = this.ctx;
    for (const s of this.sections) {
      const list: readonly MissionProgress[] = s.kind === 'achievements' ? Object.values(missions.achievements) : missions[s.kind];
      // Les missions a reclamer d'abord, les reclamees a la fin.
      const sorted = [...list].sort((a, b) => Number(a.claimed) - Number(b.claimed) || Number(b.progress >= b.target) - Number(a.progress >= a.target));
      this.reconcileRows(s, sorted);
      toggleClass(s.empty, 'is-hidden', sorted.length > 0);
      if (s.reset) {
        const ms = s.kind === 'daily' ? MS_PER_DAY - (now % MS_PER_DAY) : 4 * MS_PER_DAY + 7 * MS_PER_DAY * (weekKey(now) + 1) - now;
        setText(s.reset, i18n.t('missions.resetIn', { time: formatDuration(ms / 1000) }));
      }
    }
  }

  private reconcileRows(s: Section, items: readonly MissionProgress[]): void {
    const seen = new Set<string>();
    let ref: ChildNode | null = s.list.firstChild;
    for (const m of items) {
      seen.add(m.id);
      let node = s.cache.get(m.id);
      if (!node) {
        node = this.createRow(m);
        s.cache.set(m.id, node);
      }
      this.updateRow(node, m);
      if (node === ref) ref = ref.nextSibling;
      else s.list.insertBefore(node, ref);
    }
    for (const [id, node] of s.cache) {
      if (!seen.has(id)) {
        node.remove();
        s.cache.delete(id);
      }
    }
  }

  // ------------------------------------------------------------------------------------------------ passe

  private buildPass(pane: HTMLElement): void {
    const t = this.ctx.i18n;
    const { pass } = this.ctx.config;
    this.passLocked = h('p', { class: 'hint is-hidden' });
    this.passTier = h('b', { class: 'pass-tier-now' });
    this.passBar = h('div', { class: 'progress' });
    this.passXp = h('span', { class: 'mission-value' });
    this.passEnds = h('span', { class: 'hint' });
    this.passEpicBtn = h('button', { class: 'btn gem small', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'pass.unlockEpic' }) }, t.t('pass.unlockEpic'), ...walletChips(this.ctx, pass.epicUnlockCost));

    const legend = h('div', { class: 'pass-legend' }, h('div', { class: 'pass-legend-tier' }), h('div', { class: 'pass-legend-cell free' }, t.t('pass.free')), h('div', { class: 'pass-legend-cell epic' }, t.t('pass.epic')));
    this.passScroller = h('div', { class: 'pass-track' });
    for (const tier of pass.tiers) {
      const free = this.passCell(tier.tier, 'free', rewardChips(this.ctx, tier.free));
      const epic = this.passCell(tier.tier, 'epic', rewardChips(this.ctx, tier.epic));
      const col = h('div', { class: 'pass-col' }, h('div', { class: 'pass-tier' }, String(tier.tier)), free, epic);
      this.passCols.push(col);
      this.passScroller.append(col);
    }
    this.passBody = h(
      'div',
      { class: 'pass-body' },
      h('div', { class: 'panel pass-head' }, h('div', { class: 'pass-head-line' }, this.passTier, this.passEpicBtn), h('div', { class: 'mission-barwrap' }, this.passBar, this.passXp), this.passEnds),
      h('div', { class: 'pass-wrap' }, legend, this.passScroller),
    );
    pane.append(this.passLocked, this.passBody);
  }

  private passCell(tier: number, track: 'free' | 'epic', chips: HTMLElement[]): HTMLElement {
    const root = h('button', { class: `pass-cell ${track}`, type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'pass.claim', tier, track }) }, h('div', { class: 'pass-chips' }, ...chips), h('span', { class: 'pass-mark' }));
    this.passCells.push({ tier, track, root });
    return root;
  }

  private updatePass(): void {
    const { i18n, config, sim } = this.ctx;
    const unlocked = sim.query.feature('pass');
    toggleClass(this.passBody, 'is-hidden', !unlocked);
    toggleClass(this.passLocked, 'is-hidden', unlocked);
    if (!unlocked) {
      const need = config.featureUnlocks.pass;
      setText(this.passLocked, need.kind === 'stageValue' ? i18n.t('common.unlockAt', { stage: stageValueToDisplay(config.balance, need.atLeast).stage }) : i18n.t('common.locked'));
      return;
    }
    const { pass } = sim.data;
    const per = config.pass.xpPerTier;
    const tier = sim.query.passTier();
    setText(this.passTier, i18n.t('pass.tier', { tier }));
    const inTier = tier >= config.pass.tierCount ? per : pass.xp - tier * per;
    setVar(this.passBar, '--progress', Math.max(0, Math.min(1, inTier / per)).toFixed(3));
    setText(this.passXp, i18n.t('pass.xp', { xp: `${formatBig(inTier)}/${formatBig(per)}` }));
    const endMs = (pass.seasonId + 1) * config.pass.seasonDays * MS_PER_DAY;
    setText(this.passEnds, i18n.t('pass.endsIn', { time: formatDuration(Math.max(0, endMs - Date.now()) / 1000) }));
    toggleClass(this.passEpicBtn, 'is-hidden', pass.epicUnlocked);
    for (const c of this.passCells) {
      const claimed = (c.track === 'free' ? pass.claimedFree : pass.claimedEpic).includes(c.tier);
      const reached = c.tier <= tier;
      const lockedTrack = c.track === 'epic' && !pass.epicUnlocked;
      toggleClass(c.root, 'claimed', claimed);
      toggleClass(c.root, 'ready', !claimed && reached && !lockedTrack);
      toggleClass(c.root, 'locked', !claimed && (!reached || lockedTrack));
      toggleClass(c.root, 'lock-epic', lockedTrack);
    }
    if (!this.passScrolled && this.passScroller.clientWidth > 0) {
      this.passScrolled = true;
      const col = this.passCols[Math.max(0, Math.min(this.passCols.length - 1, tier - 1))];
      if (col) this.passScroller.scrollLeft = Math.max(0, col.offsetLeft - 8);
    }
  }

  // ------------------------------------------------------------------------------------------------ cadeau quotidien

  private buildDaily(pane: HTMLElement): void {
    const t = this.ctx.i18n;
    const grid = h('div', { class: 'daily-grid' });
    for (const d of this.ctx.config.dailyRewards) {
      const cell = h(
        'button',
        { class: 'daily-cell panel', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'daily.claim' }) },
        h('div', { class: 'daily-day' }, t.t('daily.day', { day: d.day })),
        h('div', { class: 'chips-col' }, ...rewardChips(this.ctx, d.reward)),
        h('span', { class: 'pass-mark' }),
      );
      this.dailyCells.push(cell);
      grid.append(cell);
    }
    this.dailyBtn = h('button', { class: 'btn primary big', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'daily.claim' }) }, t.t('common.claim'));
    this.dailyHint = h('p', { class: 'hint is-hidden' }, t.t('daily.comeBack'));
    pane.append(h('h3', { class: 'center' }, t.t('daily.title')), grid, this.dailyBtn, this.dailyHint);
  }

  private updateDaily(): void {
    const { query, data } = this.ctx.sim;
    const available = query.dailyAvailable();
    this.dailyCells.forEach((cell, i) => {
      const day = i + 1;
      toggleClass(cell, 'today', day === data.daily.nextDay);
      toggleClass(cell, 'claimed', day < data.daily.nextDay);
      toggleClass(cell, 'ready', available && day === data.daily.nextDay);
    });
    toggleClass(this.dailyBtn, 'is-hidden', !available);
    toggleClass(this.dailyHint, 'is-hidden', available);
  }

  // ------------------------------------------------------------------------------------------------ cycle

  private onTab(): void {
    const active = this.tabs.active();
    for (const [id, pane] of this.panes) toggleClass(pane, 'active', id === active);
    this.update(0);
  }

  override update(_dt: number): void {
    switch (this.tabs.active()) {
      case 'missions':
        this.updateMissions();
        break;
      case 'pass':
        this.updatePass();
        break;
      case 'daily':
        this.updateDaily();
        break;
    }
  }
}
